import React, { useMemo, useRef, useState } from 'react';
import { Button, Icon } from '@tviklax/ui-components';
import styled from 'styled-components';
import { Document, TimelineEntry } from './types';
import { showToast } from '../../utils/toastUtils';
import { API_BASE_URL } from '../../shared/constants/env';
import { useRole } from '../../context/RoleContext';

const DetailsContainer = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  z-index: 1000;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing[4]};
`;

const DetailsPanel = styled.div`
  background: white;
  width: 100%;
  max-width: 900px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow-y: auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    max-width: 100%;
  }
`;

const Header = styled.div`
  padding: ${({ theme }) => theme.spacing[6]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray200};
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[6]};
`;

const HeaderLeft = styled.div`
  flex: 1;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  padding: 0;

  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
  }
`;

const DocumentTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.gray900};
  margin: 0 0 ${({ theme }) => theme.spacing[2]} 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const StatusLine = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.gray600};
`;

const StatusBadge = styled.span<{ $status: 'cleared' | 'pending' | 'blocking' }>`
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $status }) => {
    switch ($status) {
      case 'cleared':
        return '#ecfdf3';
      case 'pending':
        return '#fef3c7';
      case 'blocking':
        return '#fee2e2';
      default:
        return '#f0f0f0';
    }
  }};
  color: ${({ $status }) => {
    switch ($status) {
      case 'cleared':
        return '#166534';
      case 'pending':
        return '#92400e';
      case 'blocking':
        return '#991b1b';
      default:
        return '#666';
    }
  }};
`;

const BlocksInfo = styled.div`
  font-size: 13px;
  color: #991b1b;
  margin-top: ${({ theme }) => theme.spacing[2]};
  font-weight: 500;
`;

const HeaderRight = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.gray600};
  min-width: 200px;
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MetaLabel = styled.span`
  color: ${({ theme }) => theme.colors.gray500};
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MetaValue = styled.span`
  color: ${({ theme }) => theme.colors.gray900};
  font-weight: 500;
`;

const Content = styled.div`
  padding: ${({ theme }) => theme.spacing[6]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[8]};
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray900};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding-bottom: ${({ theme }) => theme.spacing[3]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray200};
  margin: 0;
`;

const TimelineItem = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[3]} 0;

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.gray100};
  }
`;

const TimelineDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  margin-top: 6px;
  flex-shrink: 0;
`;

const TimelineContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TimelineTime = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray900};
`;

const TimelineAction = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.gray700};
`;

const TimelineActor = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.gray500};
`;

const BlockingReasonBox = styled.div`
  background: #fee2e2;
  border-left: 3px solid #dc2626;
  padding: ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const BlockingTitle = styled.div`
  font-weight: 600;
  color: #991b1b;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const BlockingText = styled.div`
  color: #7f1d1d;
  font-size: 13px;
  line-height: 1.6;
`;

const RemarkBox = styled.div`
  background: #f9fafb;
  padding: ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border-left: 3px solid ${({ theme }) => theme.colors.gray300};
`;

const RemarkLabel = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.gray500};
  font-weight: 600;
  margin-bottom: 4px;
`;

const RemarkText = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.gray700};
`;

const DownstreamImpactBox = styled.div`
  background: #fee2e2;
  border-left: 3px solid #dc2626;
  padding: ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const ImpactTitle = styled.div`
  font-weight: 700;
  color: #991b1b;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ImpactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const ImpactItem = styled.div`
  font-size: 13px;
  color: #7f1d1d;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};

  &:before {
    content: '•';
    font-weight: bold;
  }
`;

const DocumentPreviewSection = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: ${({ theme }) => theme.spacing[6]};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const PreviewBox = styled.div`
  background: #f9fafb;
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[6]};
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: ${({ theme }) => theme.colors.gray500};
  text-align: center;
  font-size: 13px;
`;

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const ActionHint = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.gray500};
  margin-top: -4px;
`;

const RevisionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const RevisionItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: #f8fafc;
`;

const RevisionMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RevisionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray900};
`;

const RevisionSub = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.gray500};
`;

const SupportingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const SupportingItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: #ffffff;
`;

const SupportingMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SupportingTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray900};
`;

const SupportingSub = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.gray500};
`;

const PreviewOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
`;

const PreviewModal = styled.div`
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const PreviewHeader = styled.div`
  padding: ${({ theme }) => theme.spacing[6]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray200};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PreviewTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray900};
  margin: 0;
`;

const PreviewContent = styled.div`
  padding: ${({ theme }) => theme.spacing[6]};
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.gray600};
`;

const PreviewIcon = styled.div`
  font-size: 64px;
`;

const PreviewFooter = styled.div`
  padding: ${({ theme }) => theme.spacing[6]};
  border-top: 1px solid ${({ theme }) => theme.colors.gray200};
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const ResponsibilityBox = styled.div`
  background: #f9fafb;
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const ResponsibilityPerson = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PersonName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray900};
`;

const PersonRole = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.gray500};
`;

const PersonContact = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const Footer = styled.div`
  padding: ${({ theme }) => theme.spacing[6]};
  border-top: 1px solid ${({ theme }) => theme.colors.gray200};
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[3]};
`;

interface DocumentDetailsProps {
  document: Document;
  onClose: () => void;
}

type RevisionKind = 'revised' | 'replace' | 'supporting';

interface RevisionEntry {
  version: number;
  fileName: string;
  kind: RevisionKind;
  uploadedAt: Date;
  uploadedBy: string;
}

interface SupportingEntry {
  fileName: string;
  uploadedAt: Date;
  uploadedBy: string;
}

interface PreviewState {
  fileName: string;
  version?: number;
  kind?: 'revision' | 'supporting';
}

export const DocumentDetails: React.FC<DocumentDetailsProps> = ({ document, onClose }) => {
  const { role } = useRole();
  // Only the party who actually owns this document (customer, supplier, or
  // an internal Tvikla team) should be able to upload a replacement for it —
  // a customer viewing a supplier-owned or internal document has no
  // legitimate corrected file to provide; their real action is to follow up
  // with whoever does.
  const isDocumentOwner = (document.owner || '').toLowerCase() === role;
  const [uploading, setUploading] = useState(false);
  const [timelineEntries, setTimelineEntries] = useState(document.timeline || []);
  const [pendingUploadAction, setPendingUploadAction] = useState<RevisionKind | null>(null);
  const [previewState, setPreviewState] = useState<PreviewState | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const baseVersion = useMemo(() => Math.max(1, document.version || 1), [document.version]);
  const [revisions, setRevisions] = useState<RevisionEntry[]>([
    {
      version: baseVersion,
      fileName: `${document.name.replace(/\s+/g, '_')}.pdf`,
      kind: 'revised',
      uploadedAt: new Date(document.lastActionTime || document.submittedAt || new Date()),
      uploadedBy: document.handledBy || '',
    },
  ]);
  const [latestFileName, setLatestFileName] = useState(revisions[0].fileName);
  const [supportingDocs, setSupportingDocs] = useState<SupportingEntry[]>([]);

  const formatDateTime = (date: Date) => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} ${hours}:${minutes}`;
  };

  const getApiOrigin = (): string => API_BASE_URL.replace(/\/graphql\/?$/, '');

  const getDownloadFileName = (url: string, fallbackName: string): string => {
    const clean = url.split('?')[0].split('#')[0];
    const candidate = clean.split('/').filter(Boolean).pop();
    if (!candidate) return fallbackName;
    try {
      return decodeURIComponent(candidate);
    } catch {
      return candidate;
    }
  };

  const buildForcedDownloadUrl = (fileUrl: string): string => {
    return `${getApiOrigin()}/api/v1/forex/download-document?fileUrl=${encodeURIComponent(fileUrl)}`;
  };

  const handlePreview = (fileName: string, version?: number, kind?: 'revision' | 'supporting') => {
    setPreviewState({ fileName, version, kind });
  };

  const handleDownload = (fileNameOverride?: string) => {
    if (!document.fileUrl) {
      showToast(`No uploaded file available for ${document.name}`, 'error');
      return;
    }

    const fallbackName = fileNameOverride || `${document.id}-${document.name.replace(/\s+/g, '_')}`;
    const resolvedName = getDownloadFileName(document.fileUrl, fallbackName);

    const link = window.document.createElement('a');
    link.href = buildForcedDownloadUrl(document.fileUrl);
    link.download = resolvedName;
    link.rel = 'noopener noreferrer';
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    showToast(`Download started for ${resolvedName}`, 'info');

    setPreviewState(null);
  };

  const triggerFileSelect = (action: RevisionKind) => {
    setPendingUploadAction(action);
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !pendingUploadAction) return;

    setUploading(true);

    const actionCopy = {
      revised: 'Uploaded revised version',
      replace: 'Replaced current document',
      supporting: 'Added supporting document',
    }[pendingUploadAction];

    const nextVersion = (revisions[0]?.version || baseVersion) + 1;

    setTimeout(() => {
      setTimelineEntries((prev: TimelineEntry[]) => [
        {
          date: new Date(),
          action: `${actionCopy}: ${file.name}`,
          actor: 'You',
          role: 'Operator',
        },
        ...prev,
      ]);

      if (pendingUploadAction === 'supporting') {
        setSupportingDocs((prev) => [
          {
            fileName: file.name,
            uploadedAt: new Date(),
            uploadedBy: 'You',
          },
          ...prev,
        ]);
      } else {
        setRevisions((prev) => [
          {
            version: nextVersion,
            fileName: file.name,
            kind: pendingUploadAction,
            uploadedAt: new Date(),
            uploadedBy: 'You',
          },
          ...prev,
        ]);

        setLatestFileName(file.name);
      }

      const toastMessage =
        pendingUploadAction === 'supporting'
          ? `Supporting document added: ${file.name}`
          : `${actionCopy} (${file.name})`;

      showToast(toastMessage, 'success');
      setUploading(false);
      setPendingUploadAction(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }, 900);
  };

  const statusLabel = {
    cleared: 'CLEARED',
    pending: 'PENDING',
    blocking: 'BLOCKING',
  }[document.status];

  return (
    <DetailsContainer onClick={onClose}>
      <DetailsPanel onClick={(e) => e.stopPropagation()}>
        <Header>
          <HeaderLeft>
            <BackButton onClick={onClose}>
              {'<-'}
              Back to Documents
            </BackButton>
            <DocumentTitle>
              {document.name}
              <StatusBadge $status={document.status}>🔴 {statusLabel}</StatusBadge>
            </DocumentTitle>
              <StatusLine>
                {document.status === 'blocking'
                  ? 'Status: Blocking · Awaiting approval from issuer/source'
                  : document.status === 'cleared'
                    ? 'Status: Valid · Does not block shipment'
                    : 'Status: Pending review · Does not block shipment yet'}
              </StatusLine>
            {document.status === 'blocking' && (
              <BlocksInfo>
                ⚠️ Blocks: Customs clearance → Shipment release → Payment settlement
              </BlocksInfo>
            )}
          </HeaderLeft>
          <HeaderRight>
            <MetaItem>
              <MetaLabel>Linked to</MetaLabel>
              <MetaValue>
                {document.orderId}
                {document.shipmentId && ` → ${document.shipmentId}`}
              </MetaValue>
            </MetaItem>
            {document.dueDate && (
              <MetaItem>
                <MetaLabel>Due date</MetaLabel>
                <MetaValue>{new Date(document.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</MetaValue>
              </MetaItem>
            )}
          </HeaderRight>
        </Header>

        <Content>
          {/* Timeline */}
          <Section>
            <SectionTitle>Document Activity</SectionTitle>
            <div>
              {timelineEntries && timelineEntries.length > 0 ? (
                timelineEntries.map((item, idx) => (
                  <TimelineItem key={idx}>
                    <TimelineDot />
                    <TimelineContent>
                      <TimelineTime>{formatDateTime(item.date)}</TimelineTime>
                      <TimelineAction>{item.action}</TimelineAction>
                      <TimelineActor>{item.actor} {item.role && `(${item.role})`}</TimelineActor>
                    </TimelineContent>
                  </TimelineItem>
                ))
              ) : (
                <div style={{ color: '#64748b', fontSize: '13px', lineHeight: 1.6 }}>
                  No activity recorded yet. Activity such as uploads, reviews, approvals, and comments will appear here.
                </div>
              )}
            </div>
          </Section>

          {/* Blocking Reason */}
          {document.status === 'blocking' && (
            <Section>
              <BlockingReasonBox>
                <BlockingTitle>⚠️ Why this document is blocking</BlockingTitle>
                <BlockingText>
                  {document.name} is required for customs clearance. The shipment cannot proceed until {document.name.toLowerCase()} is approved by the issuer/source.
                </BlockingText>
                <BlockingText style={{ fontWeight: 600 }}>
                  Next step: Upload a corrected or approved version of {document.name}, or contact the issuer/source to resolve it.
                </BlockingText>
                {document.authorityRemark && (
                  <RemarkBox>
                    <RemarkLabel>Source note</RemarkLabel>
                    <RemarkText>{document.authorityRemark}</RemarkText>
                  </RemarkBox>
                )}
              </BlockingReasonBox>
            </Section>
          )}

          {/* Downstream Impact */}
          {document.status === 'blocking' && (
            <Section>
              <DownstreamImpactBox>
                <ImpactTitle>⚠️ Downstream Impact</ImpactTitle>
                <div style={{ fontSize: '13px', color: '#7f1d1d', lineHeight: 1.6 }}>
                  This document currently blocks:
                </div>
                <ImpactList>
                  <ImpactItem>Customs clearance</ImpactItem>
                  <ImpactItem>Shipment release</ImpactItem>
                  <ImpactItem>Payment settlement</ImpactItem>
                </ImpactList>
              </DownstreamImpactBox>
            </Section>
          )}

          {/* Revisions */}
          <Section>
            <SectionTitle>Revisions & Versions</SectionTitle>
            <RevisionsList>
              {revisions.map((rev, idx) => (
                <RevisionItem key={`${rev.version}-${idx}`}>
                  <RevisionMeta>
                    <RevisionTitle>
                      v{rev.version} · {rev.fileName}
                      {idx === 0 && ' (latest)'}
                    </RevisionTitle>
                    <RevisionSub>
                      {rev.kind === 'replace' ? 'Replaced current document' : 'Revised version'} · {rev.uploadedBy}
                    </RevisionSub>
                    <RevisionSub>
                      {rev.uploadedAt.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </RevisionSub>
                  </RevisionMeta>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handlePreview(rev.fileName, rev.version, 'revision')}
                  >
                    Preview / Download
                  </Button>
                </RevisionItem>
              ))}
            </RevisionsList>
          </Section>

          {/* Supporting Documents */}
          <Section>
            <SectionTitle>Supporting Documents</SectionTitle>
            <SupportingList>
              {supportingDocs.length === 0 ? (
                <SupportingSub>No supporting documents yet. Upload to provide evidence.</SupportingSub>
              ) : (
                supportingDocs.map((supp, idx) => (
                  <SupportingItem key={`${supp.fileName}-${idx}`}>
                    <SupportingMeta>
                      <SupportingTitle>{supp.fileName}</SupportingTitle>
                      <SupportingSub>
                        Uploaded by {supp.uploadedBy} · {supp.uploadedAt.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </SupportingSub>
                    </SupportingMeta>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePreview(supp.fileName, undefined, 'supporting')}
                    >
                      Preview / Download
                    </Button>
                  </SupportingItem>
                ))
              )}
            </SupportingList>
            <div>
              <Button
                variant="outline"
                size="sm"
                disabled={uploading}
                onClick={() => triggerFileSelect('supporting')}
              >
                {uploading && pendingUploadAction === 'supporting' ? 'Uploading...' : '➕ Add Supporting Document'}
              </Button>
              <ActionHint style={{ marginTop: '6px' }}>Does not overwrite main document</ActionHint>
            </div>
          </Section>

          {/* Document Preview + Upload */}
          <Section>
            <SectionTitle>Document & Actions</SectionTitle>
            <DocumentPreviewSection>
              <PreviewBox>
                <div>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>📄</div>
                  <div>Latest file: {latestFileName}</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}>Click Preview in revisions to open</div>
                </div>
              </PreviewBox>
              <ActionButtons>
                {isDocumentOwner ? (
                  <>
                    <Button
                      variant="default"
                      size="sm"
                      disabled={uploading}
                      onClick={() => triggerFileSelect('revised')}
                      style={{ width: '100%' }}
                    >
                      {uploading && pendingUploadAction === 'revised' ? 'Uploading...' : '📤 Upload Revised Version'}
                    </Button>
                    <ActionHint>Keeps history; adds a new version</ActionHint>
                    <Button
                      variant="outline"
                      size="sm"
                      style={{ width: '100%' }}
                      disabled={uploading}
                      onClick={() => triggerFileSelect('replace')}
                    >
                      {uploading && pendingUploadAction === 'replace' ? 'Uploading...' : '🔄 Replace Document'}
                    </Button>
                    <ActionHint>Overwrites current version</ActionHint>
                  </>
                ) : (
                  <ActionHint>
                    Only {document.issuedBy || 'the document owner'} can upload a new version of this document.
                  </ActionHint>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
              </ActionButtons>
            </DocumentPreviewSection>
          </Section>

          {/* Document Owner */}
          <Section>
            <SectionTitle>Document Owner</SectionTitle>
            <ResponsibilityBox>
              <div>
                <ResponsibilityPerson>
                  <PersonName>{document.issuedBy}</PersonName>
                  {document.handledByRole && <PersonRole>{document.handledByRole}</PersonRole>}
                  {document.handledByEmail && <PersonContact>{document.handledByEmail}</PersonContact>}
                  {document.handledByPhone && (
                    <PersonContact>
                      {document.handledByPhone}
                      <a href={`tel:${document.handledByPhone}`} style={{ marginLeft: '8px', color: 'inherit', display: 'inline-flex', verticalAlign: 'middle' }} aria-label={`Call ${document.handledBy || document.issuedBy}`}>
                        <Icon name="Phone" size={14} />
                      </a>
                    </PersonContact>
                  )}
                  {(document.handledByOrganization || document.handledByCountry) && (
                    <ActionHint style={{ marginTop: '2px' }}>
                      {[document.handledByOrganization, document.handledByCountry].filter(Boolean).join(' · ')}
                    </ActionHint>
                  )}
                </ResponsibilityPerson>
              </div>
            </ResponsibilityBox>
          </Section>

          {/* Handlers */}
          <Section>
            <SectionTitle>Handlers</SectionTitle>
            <ResponsibilityBox>
              <div>
                {document.handledBy ? (
                  <ResponsibilityPerson>
                    <PersonName>⭐ {document.handledBy}</PersonName>
                    {document.handledByRole && <PersonRole>{document.handledByRole}</PersonRole>}
                    {document.handledByEmail && <PersonContact>{document.handledByEmail}</PersonContact>}
                    {document.handledByPhone && (
                      <PersonContact>
                        {document.handledByPhone}
                        <a href={`tel:${document.handledByPhone}`} style={{ marginLeft: '8px', color: 'inherit', display: 'inline-flex', verticalAlign: 'middle' }} aria-label={`Call ${document.handledBy}`}>
                          <Icon name="Phone" size={14} />
                        </a>
                      </PersonContact>
                    )}
                    {(document.handledByOrganization || document.handledByCountry) && (
                      <ActionHint style={{ marginTop: '2px' }}>
                        {[document.handledByOrganization, document.handledByCountry].filter(Boolean).join(' · ')}
                      </ActionHint>
                    )}
                  </ResponsibilityPerson>
                ) : (
                  <ActionHint>No mapped handler found in contacts table for this document.</ActionHint>
                )}
              </div>
            </ResponsibilityBox>
          </Section>
        </Content>

        <Footer>
          <Button variant="outline" size="sm" onClick={onClose}>
            Back to Documents
          </Button>
          <Button variant="outline" size="sm" onClick={() => { handleDownload(); }} disabled={!document.fileUrl}>
            <Icon name="Download" size={14} style={{ marginRight: '6px' }} />
            Download
          </Button>
        </Footer>
      </DetailsPanel>

      {/* Preview Modal */}
      {previewState && (
        <PreviewOverlay onClick={() => setPreviewState(null)}>
          <PreviewModal onClick={(e) => e.stopPropagation()}>
            <PreviewHeader>
              <PreviewTitle>Preview: {previewState.fileName}</PreviewTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPreviewState(null)}
              >
                ✕
              </Button>
            </PreviewHeader>
            <PreviewContent>
              <PreviewIcon>📄</PreviewIcon>
              <div style={{ textAlign: 'center', fontSize: '14px', color: '#64748b' }}>
                <div style={{ fontWeight: 600, marginBottom: '6px' }}>
                  {previewState.fileName}
                </div>
                <div style={{ fontSize: '13px' }}>
                  {previewState.kind === 'supporting' ? 'Supporting document' : `Version ${previewState.version}`}
                </div>
                <div style={{ fontSize: '12px', marginTop: '8px', color: '#94a3b8' }}>
                  File preview is not available for this document type
                </div>
              </div>
            </PreviewContent>
            <PreviewFooter>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPreviewState(null)}
              >
                Close
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  handleDownload(previewState.fileName);
                }}
                disabled={!document.fileUrl}
              >
                Download
              </Button>
            </PreviewFooter>
          </PreviewModal>
        </PreviewOverlay>
      )}    </DetailsContainer>
  );
};